import { HttpErrorResponse } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { auditTime, BehaviorSubject, filter, map, merge, Observable, Subscription } from "rxjs";
import { AssessmentTasksService } from "./assessment_tasks.service";
import { AssessmentTaskInvalidationService } from "./assessment_task_invalidation.service";
import { EvidenceRequirementInvalidationService } from "./evidence_requirement_invalidation.service";
import { AssessmentWorkItemsService } from "./assessment_work_items.service";
import type { AssessmentWorkItem } from "./assessment_work_items.types";
import {
    allScopeHeaderWorkItemFilters, changeHeaderWorkItemFilters, initialHeaderWorkItemFilters
} from "./assessment_task_header_filters";
import type { HeaderWorkItemFilterChanges, HeaderWorkItemFilters } from "./assessment_task_header_filters";
import type {
    AssessmentTaskListFilters, AssessmentTaskPage, AssessmentTaskRequirementRef, AssessmentTaskStatus, AssessmentTaskSummary
} from "./assessment_tasks.types";
import { LoginService } from "./login.service";
import { assessmentTaskSessionIdentity } from "./assessment_task_session.util";
import { isAssessmentTaskStatus } from "../utils/assessment_task_status.util";

export type AssessmentTaskLaunchContext =
    | { kind: "header" }
    | { kind: "assessment"; productLicenseId: number; statuses?: readonly AssessmentTaskStatus[] }
    | { kind: "requirement"; productLicenseId: number; requirement: AssessmentTaskRequirementRef }
    | { kind: "task"; productLicenseId: number; taskId: number };

export interface AssessmentTaskLaunchState {
    isOpen: boolean;
    activeAssessmentId: number | null;
    context: AssessmentTaskLaunchContext | null;
    filters: AssessmentTaskListFilters;
    items: AssessmentTaskSummary[];
    total: number;
    isLoading: boolean;
    error: string | null;
    combinedHeader: AssessmentTaskHeaderState | null;
}

export interface AssessmentTaskHeaderState {
    filters: HeaderWorkItemFilters;
    items: AssessmentWorkItem[];
    total: number;
    isLoading: boolean;
    error: string | null;
}

const DEFAULT_PAGE_SIZE = 25;

const positiveId = (value: unknown): value is number =>
    typeof value === "number" && Number.isSafeInteger(value) && value > 0;

const initialState = (activeAssessmentId: number | null = null): AssessmentTaskLaunchState => ({
    isOpen: false,
    activeAssessmentId,
    context: null,
    filters: { page: 1, pageSize: DEFAULT_PAGE_SIZE, sort: "due_date_desc" },
    items: [],
    total: 0,
    isLoading: false,
    error: null,
    combinedHeader: null
});

const initialHeaderState = (): AssessmentTaskHeaderState => ({
    filters: initialHeaderWorkItemFilters(),
    items: [],
    total: 0,
    isLoading: false,
    error: null
});

const filtersForContext = (context: AssessmentTaskLaunchContext): AssessmentTaskListFilters => {
    const base: AssessmentTaskListFilters = { page: 1, pageSize: DEFAULT_PAGE_SIZE, sort: "due_date_desc" };
    switch (context.kind) {
        case "header":
            return { ...base, assignedToMe: true };
        case "assessment":
            return {
                ...base,
                assessmentIds: [context.productLicenseId],
                ...(context.statuses?.length ? { statuses: [...context.statuses] } : {})
            };
        case "requirement":
            return {
                ...base,
                assessmentIds: [context.productLicenseId],
                requirement: { ...context.requirement }
            };
        case "task":
            return { ...base, assessmentIds: [context.productLicenseId], taskId: context.taskId };
    }
};

@Injectable({ providedIn: "root" })
export class AssessmentTaskLaunchService implements OnDestroy {
    private readonly state = new BehaviorSubject<AssessmentTaskLaunchState>(initialState());
    private readonly subscriptions = new Subscription();
    private pageSubscription: Subscription | null = null;
    private openTimer: ReturnType<typeof setTimeout> | null = null;
    private requestVersion = 0;
    private currentSessionIdentity: string | null = null;
    private currentSessionToken: string | null = null;

    constructor(
        private readonly tasks: AssessmentTasksService,
        private readonly workItems: AssessmentWorkItemsService,
        private readonly invalidations: AssessmentTaskInvalidationService,
        private readonly evidenceInvalidations: EvidenceRequirementInvalidationService,
        private readonly login: LoginService
    ) {
        this.subscriptions.add(login.getUserUpdates().subscribe((user) => {
            const nextIdentity = assessmentTaskSessionIdentity(user);
            const nextToken = login.getToken();
            if (nextIdentity !== this.currentSessionIdentity || nextToken !== this.currentSessionToken) {
                this.currentSessionIdentity = nextIdentity;
                this.currentSessionToken = nextToken;
                this.clearState(false);
            }
        }));
        this.subscriptions.add(merge(
            this.invalidations.watchInvalidations().pipe(map((event) => ({ source: "task" as const, event }))),
            this.evidenceInvalidations.watchChanges().pipe(map((event) => ({ source: "evidence" as const, event })))
        ).pipe(
            filter((hint) => {
                const current = this.state.value;
                if (!current.isOpen) return false;
                if (hint.source === "evidence") {
                    if (current.context?.kind !== "header" ||
                        hint.event.accountId !== this.login.getCurrentAccountId() ||
                        current.combinedHeader?.filters.itemType === "task") return false;
                } else if (current.context?.kind === "header" &&
                    current.combinedHeader?.filters.itemType === "evidence" &&
                    hint.event.kind !== "scope") return false;
                const assessmentIds = current.context?.kind === "header"
                    ? current.combinedHeader?.filters.assessmentIds : current.filters.assessmentIds;
                return hint.event.productLicenseId === null || !assessmentIds?.length ||
                    assessmentIds.includes(hint.event.productLicenseId);
            }),
            map(() => ({
                filters: this.state.value.context?.kind === "header"
                    ? this.state.value.combinedHeader?.filters : this.state.value.filters,
                context: this.state.value.context,
                sessionIdentity: this.currentSessionIdentity,
                token: this.login.getToken()
            })),
            auditTime(0)
        ).subscribe(({ filters, context, sessionIdentity, token }) => {
            const current = this.state.value;
            const currentFilters = current.context?.kind === "header"
                ? current.combinedHeader?.filters : current.filters;
            if (current.isOpen && context === current.context && filters === currentFilters &&
                sessionIdentity === this.currentSessionIdentity && token === this.login.getToken()) this.refresh();
        }));
    }

    watchState(): Observable<AssessmentTaskLaunchState> {
        return this.state.asObservable();
    }

    getSnapshot(): AssessmentTaskLaunchState {
        return this.state.value;
    }

    openHeader(): void {
        this.open({ kind: "header" });
    }

    openAssessment(productLicenseId: number, statuses?: readonly AssessmentTaskStatus[]): void {
        this.open({ kind: "assessment", productLicenseId, ...(statuses ? { statuses } : {}) });
    }

    openRequirement(productLicenseId: number, requirement: AssessmentTaskRequirementRef): void {
        this.open({ kind: "requirement", productLicenseId, requirement });
    }

    openTask(productLicenseId: number, taskId: number): void {
        this.open({ kind: "task", productLicenseId, taskId });
    }

    open(context: AssessmentTaskLaunchContext): void {
        if (!this.currentSessionIdentity || !this.currentSessionToken ||
            this.currentSessionToken !== this.login.getToken()) {
            this.clearState(false);
            return;
        }
        if (context.kind !== "header" && !positiveId(context.productLicenseId)) {
            throw new Error("A valid assessment ID is required to open tasks.");
        }
        if (context.kind === "assessment" && context.statuses !== undefined &&
            (!Array.isArray(context.statuses) || context.statuses.length > 4 ||
                context.statuses.some((status) => !isAssessmentTaskStatus(status)))) {
            throw new Error("Valid task statuses are required.");
        }
        if (context.kind === "task" && !positiveId(context.taskId)) {
            throw new Error("A valid task ID is required.");
        }
        if (context.kind === "requirement" &&
            (typeof context.requirement?.sectionKey !== "string" || !context.requirement.sectionKey ||
                typeof context.requirement?.requirementKey !== "string" || !context.requirement.requirementKey)) {
            throw new Error("A canonical requirement is required.");
        }
        this.cancelPageRequest();
        const openVersion = this.requestVersion;
        const launchContext: AssessmentTaskLaunchContext = context.kind === "requirement"
            ? { ...context, requirement: { ...context.requirement } }
            : context.kind === "assessment"
                ? { ...context, ...(context.statuses ? { statuses: [...context.statuses] } : {}) }
                : { ...context };
        this.state.next({
            ...initialState(this.state.value.activeAssessmentId),
            isOpen: true,
            isLoading: launchContext.kind !== "header",
            context: launchContext,
            filters: filtersForContext(launchContext),
            combinedHeader: launchContext.kind === "header"
                ? { ...initialHeaderState(), isLoading: true } : null
        });
        // Let the modal render its initial loading state before starting the list read.
        this.openTimer = setTimeout(() => {
            this.openTimer = null;
            if (this.requestVersion === openVersion && this.state.value.isOpen &&
                this.state.value.context === launchContext) this.refresh();
        }, 0);
    }

    setFilters(changes: Partial<AssessmentTaskListFilters>): void {
        if (!this.state.value.isOpen || this.state.value.context?.kind === "header") return;
        const filters = { ...this.state.value.filters, ...changes, page: 1 };
        this.state.next({ ...this.state.value, filters });
        this.refresh();
    }

    // Keep combined header filters separate from task-only browser filters.
    setHeaderFilters(changes: HeaderWorkItemFilterChanges): void {
        const current = this.state.value;
        if (!current.isOpen || current.context?.kind !== "header" || !current.combinedHeader) return;
        this.state.next({ ...current, combinedHeader: {
            ...current.combinedHeader,
            filters: changeHeaderWorkItemFilters(current.combinedHeader.filters, changes)
        } });
        this.refreshHeader();
    }

    setHeaderPage(page: number): void {
        const current = this.state.value;
        if (!current.isOpen || current.context?.kind !== "header" || !current.combinedHeader) return;
        if (!positiveId(page)) throw new Error("A valid work-item page is required.");
        this.state.next({ ...current, combinedHeader: {
            ...current.combinedHeader,
            filters: { ...current.combinedHeader.filters, page }
        } });
        this.refreshHeader();
    }

    clearHeaderFilters(): void {
        const current = this.state.value;
        if (!current.isOpen || current.context?.kind !== "header" || !current.combinedHeader) return;
        this.state.next({ ...current, combinedHeader: {
            ...current.combinedHeader,
            filters: allScopeHeaderWorkItemFilters()
        } });
        this.refreshHeader();
    }

    refreshHeader(): void {
        const current = this.state.value;
        if (!current.isOpen || current.context?.kind !== "header" ||
            !current.combinedHeader || !this.currentSessionIdentity || !this.currentSessionToken ||
            this.currentSessionToken !== this.login.getToken()) return;
        this.cancelPageRequest();
        const requestVersion = this.requestVersion;
        const identity = this.currentSessionIdentity;
        const token = this.login.getToken();
        const filters = { ...current.combinedHeader.filters };
        this.state.next({ ...current, combinedHeader: {
            ...current.combinedHeader, isLoading: true, error: null
        } });
        try {
            this.pageSubscription = this.workItems.listWorkItems(filters).subscribe({
                next: (page) => {
                    if (!this.isCurrentHeaderRequest(requestVersion, identity, token)) return;
                    const lastPage = Math.max(1, Math.ceil(page.total / page.pageSize));
                    if (filters.page > lastPage) {
                        this.setHeaderPage(lastPage);
                        return;
                    }
                    const state = this.state.value;
                    const header = state.combinedHeader;
                    if (!header) return;
                    const nextFilters = header.filters.page === page.page &&
                        header.filters.pageSize === page.pageSize
                        ? header.filters
                        : { ...header.filters, page: page.page, pageSize: page.pageSize };
                    this.state.next({ ...state, combinedHeader: {
                        filters: nextFilters,
                        items: page.items,
                        total: page.total,
                        isLoading: false,
                        error: null
                    } });
                },
                error: (error: unknown) => this.showHeaderRequestError(error, requestVersion, identity, token)
            });
        } catch (error) {
            this.showHeaderRequestError(error, requestVersion, identity, token);
        }
    }

    setPage(page: number): void {
        if (!this.state.value.isOpen) return;
        if (this.state.value.context?.kind === "header") {
            this.setHeaderPage(page);
            return;
        }
        if (!positiveId(page)) throw new Error("A valid task page is required.");
        this.state.next({ ...this.state.value, filters: { ...this.state.value.filters, page } });
        this.refresh();
    }

    clearFilters(): void {
        if (!this.state.value.isOpen) return;
        if (this.state.value.context?.kind === "header") {
            this.clearHeaderFilters();
            return;
        }
        // Task-only scopes use their own neutral all-in-scope filter set.
        this.state.next({ ...this.state.value, filters: {
            page: 1, pageSize: DEFAULT_PAGE_SIZE, sort: this.state.value.filters.sort || "due_date_desc"
        } });
        this.refresh();
    }

    resetContextFilters(): void {
        const context = this.state.value.context;
        if (!this.state.value.isOpen || !context) return;
        if (context.kind === "header") {
            const header = this.state.value.combinedHeader;
            if (!header) return;
            this.state.next({ ...this.state.value, combinedHeader: {
                ...header, filters: initialHeaderWorkItemFilters()
            } });
            this.refreshHeader();
            return;
        }
        this.state.next({ ...this.state.value, filters: filtersForContext(context) });
        this.refresh();
    }

    setActiveAssessment(productLicenseId: number | null): void {
        const nextId = positiveId(productLicenseId) ? productLicenseId : null;
        if (nextId === this.state.value.activeAssessmentId) return;
        this.cancelPageRequest();
        this.state.next(initialState(nextId));
    }

    close(): void {
        this.clearState();
    }

    refresh(): void {
        const current = this.state.value;
        if (!current.isOpen || !this.currentSessionIdentity || !this.currentSessionToken ||
            this.currentSessionToken !== this.login.getToken()) return;
        if (current.context?.kind === "header") {
            this.refreshHeader();
            return;
        }
        this.cancelPageRequest();
        const requestVersion = this.requestVersion;
        const identity = this.currentSessionIdentity;
        const token = this.login.getToken();
        const filters = { ...current.filters };
        this.state.next({ ...current, isLoading: true, error: null });
        let request: Observable<AssessmentTaskPage<AssessmentTaskSummary>>;
        try {
            request = this.tasks.listTasks(filters);
        } catch (error) {
            this.showRequestError(error, requestVersion, identity, token);
            return;
        }
        this.pageSubscription = request.subscribe({
            next: (page) => {
                if (!this.isCurrentRequest(requestVersion, identity, token)) return;
                const lastPage = Math.max(1, Math.ceil(page.total / page.pageSize));
                if ((filters.page || 1) > lastPage) {
                    this.setPage(lastPage);
                    return;
                }
                const currentFilters = this.state.value.filters;
                const nextFilters = currentFilters.page === page.page && currentFilters.pageSize === page.pageSize
                    ? currentFilters
                    : { ...currentFilters, page: page.page, pageSize: page.pageSize };
                this.state.next({
                    ...this.state.value,
                    items: page.items,
                    total: page.total,
                    filters: nextFilters,
                    isLoading: false,
                    error: null
                });
            },
            error: (error: unknown) => this.showRequestError(error, requestVersion, identity, token)
        });
    }

    ngOnDestroy(): void {
        this.cancelPageRequest();
        this.subscriptions.unsubscribe();
        this.state.complete();
    }

    private isCurrentRequest(version: number, identity: string, token: string | null): boolean {
        return version === this.requestVersion && identity === this.currentSessionIdentity &&
            token === this.currentSessionToken && token === this.login.getToken() && this.state.value.isOpen;
    }

    private isCurrentHeaderRequest(version: number, identity: string, token: string | null): boolean {
        return this.isCurrentRequest(version, identity, token) &&
            this.state.value.context?.kind === "header" && this.state.value.combinedHeader !== null;
    }

    private showHeaderRequestError(error: unknown, version: number, identity: string, token: string | null): void {
        if (!this.isCurrentHeaderRequest(version, identity, token)) return;
        const state = this.state.value;
        const header = state.combinedHeader;
        if (!header) return;
        const message = error instanceof HttpErrorResponse && (error.status === 403 || error.status === 404)
            ? "Assigned work is unavailable in your current scope."
            : "Unable to load assigned work.";
        this.state.next({ ...state, combinedHeader: {
            ...header, items: [], total: 0, isLoading: false, error: message
        } });
    }

    private showRequestError(error: unknown, version: number, identity: string, token: string | null): void {
        if (!this.isCurrentRequest(version, identity, token)) return;
        const exactTaskUnavailable = positiveId(this.state.value.filters.taskId) &&
            error instanceof HttpErrorResponse && (error.status === 403 || error.status === 404);
        const serverMessage = error instanceof HttpErrorResponse &&
            error.error && typeof error.error === "object" &&
            typeof error.error.message === "string"
            ? error.error.message : null;
        const unavailableMessage = exactTaskUnavailable ? "This task is unavailable in your current scope." :
            serverMessage || (error instanceof HttpErrorResponse
                ? "Unable to load assessment tasks."
                : error instanceof Error ? error.message : "Unable to load assessment tasks.");
        this.state.next({
            ...this.state.value,
            items: [],
            total: 0,
            isLoading: false,
            error: unavailableMessage
        });
    }

    private clearState(retainAssessment = true): void {
        this.cancelPageRequest();
        this.state.next(initialState(retainAssessment ? this.state.value.activeAssessmentId : null));
    }

    private cancelPageRequest(): void {
        if (this.openTimer !== null) {
            clearTimeout(this.openTimer);
            this.openTimer = null;
        }
        this.requestVersion += 1;
        this.pageSubscription?.unsubscribe();
        this.pageSubscription = null;
    }
}
